import { TableCell, TableRow, Link as FluentLink, Button, Tooltip, Body1Strong } from '@fluentui/react-components';
import { bundleIcon, EditFilled, EditRegular } from '@fluentui/react-icons';

import { ControlledTooltip, DateLabel, SmartTableCell, TableActionButtons } from '@shared/components';
import { getItemPathByItemType } from '@modules/documents';
import { TSelectableTableRow } from '@shared/types';
import { Avatar, useUsers } from '@modules/users';
import { ESubmissionStatus, TResolvedSubmissionItem, TSubmission } from '../../../types/submissions.types';
import SubmissionItemStatus from './SubmissionItemStatus';
import { TUpdateSubmissionItemDialogState } from '../../../types/dialogs.types';
import { ITableColumnSizingState } from '@shared/types';
import { buildHref } from '@shared/utils';

const EditIcon = bundleIcon(EditFilled, EditRegular);

type TProps = TSelectableTableRow<TResolvedSubmissionItem> & {
  submission: TSubmission;
  filteredResolvedSelectedItems: TResolvedSubmissionItem[];
  setUpdateSubmissionItemDialogState: (state?: TUpdateSubmissionItemDialogState) => void;
  columnSizing?: ITableColumnSizingState;
};

const SubmissionItem = ({
  submission,
  filteredResolvedSelectedItems,
  setUpdateSubmissionItemDialogState,
  selectionCell,
  item: resolvedSubmissionItem,
  preventSelect,
  columnSizing,
  ...tableRowAttributes
}: TProps) => {
  const { resolvedItem } = resolvedSubmissionItem;

  const { userMap } = useUsers();

  const ItemNameEditButton = () => {
    return (
      <TableActionButtons alwaysVisible={tableRowAttributes.alwaysVisible}>
        <ControlledTooltip
          enable={submission.status !== ESubmissionStatus.IN_PREPARATION}
          content="Submission is not in preparation."
          relationship="inaccessible"
        >
          <Button
            icon={<EditIcon />}
            appearance="subtle"
            className={tableRowAttributes.actionButtonClassName}
            disabled={submission.status !== ESubmissionStatus.IN_PREPARATION}
            aria-label="Edit submission item"
            onClick={(e) => {
              setUpdateSubmissionItemDialogState({
                isOpen: true,
                submissionInfo: submission,
                submissionItem: resolvedSubmissionItem,
                item: resolvedSubmissionItem.resolvedItem,
              });
              preventSelect?.(e);
            }}
          />
        </ControlledTooltip>
      </TableActionButtons>
    );
  };

  return (
    <TableRow
      {...tableRowAttributes}
      style={{
        borderRadius: '4px',
      }}
    >
      {selectionCell}

      {/* 1) Name column */}
      <SmartTableCell
        cellProps={{ ...(columnSizing?.getTableCellProps('name') ?? {}) }}
        variant={{ kind: 'item', item: resolvedItem }}
        after={<ItemNameEditButton />}
      />
      <TableCell {...columnSizing?.getTableCellProps('status')}>
        <SubmissionItemStatus
          submission={submission}
          resolvedSubmissionItem={resolvedSubmissionItem}
          setUpdateSubmissionItemDialogState={setUpdateSubmissionItemDialogState}
        />
      </TableCell>
      <TableCell {...columnSizing?.getTableCellProps('assignee')}>
        {resolvedSubmissionItem.assignee && (
          <Avatar user={userMap && userMap[resolvedSubmissionItem.assignee]} tooltip="name" />
        )}
      </TableCell>
      <TableCell {...columnSizing?.getTableCellProps('dueDate')}>
        {resolvedSubmissionItem.dueDate && <DateLabel>{resolvedSubmissionItem.dueDate}</DateLabel>}
      </TableCell>
      <TableCell {...columnSizing?.getTableCellProps('submittedVersion')}>
        {resolvedSubmissionItem.submittedVersionId ? (
          <FluentLink
            href={buildHref(`/${getItemPathByItemType(resolvedItem.type)}/${resolvedItem._id}`, {
              view: resolvedSubmissionItem.submittedVersionId,
            })}
            target="_blank"
            onClick={preventSelect}
          >
            <Body1Strong>{resolvedSubmissionItem.submittedVersionId}</Body1Strong>
          </FluentLink>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell {...columnSizing?.getTableCellProps('latestVersion')}>
        <div className="flex space-x-2 items-center">
          {resolvedItem.stats.latestVersionId ? (
            <FluentLink
              href={buildHref(`/${getItemPathByItemType(resolvedItem.type)}/${resolvedItem._id}`, {
                view: resolvedItem.stats.latestVersionId,
              })}
              target="_blank"
              onClick={preventSelect}
            >
              {resolvedItem.stats.latestVersionId}
            </FluentLink>
          ) : (
            <span>-</span>
          )}
          {resolvedItem.hasDraft && (
            <Tooltip content={`This item has an open draft.`} relationship="label">
              <EditRegular />
            </Tooltip>
          )}
        </div>
      </TableCell>
      {/* 4) Approved version column */}
      <TableCell {...columnSizing?.getTableCellProps('approvedVersion')}>
        {resolvedItem.stats.approvedVersionId ? (
          <FluentLink
            href={buildHref(`/${getItemPathByItemType(resolvedItem.type)}/${resolvedItem._id}`, {
              view: resolvedItem.stats.approvedVersionId,
            })}
            target="_blank"
            onClick={preventSelect}
          >
            {resolvedItem.stats.approvedVersionId}
          </FluentLink>
        ) : (
          '-'
        )}
      </TableCell>
    </TableRow>
  );
};

export default SubmissionItem;
